import { useEffect, useState, useCallback, useRef } from "react";
import {
  Loader2, Search, Plus, Trash2, FileClock, Truck, CheckCircle2, Ban,
  ClipboardList, Clock, PackageCheck, AlertTriangle, Paperclip, Upload, Info, Store, Package, Split, FileDown, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, rupiahShort, fmtDate, fmtDateTime, slug } from "@/lib/format";
import { Modal } from "@/components/Modal";
import { CustomerPickerDialog } from "@/components/CustomerPicker";
import { CloseOrderDialog } from "@/components/orders/CloseOrderDialog";
import { CustomerArWarning } from "@/components/finance/CustomerArCard";
import { DateField } from "@/components/DateField";
import { DocMenu, DocActions, printRaw, openPdf } from "@/components/docs/DocMenu";
import { BulkPrintBar } from "@/components/docs/BulkPrintBar";
import { PickingListDialog } from "@/components/docs/PickingListDialog";
import { AuditTimeline } from "@/components/orders/AuditTimeline";
import { getPrinter, getAutoPrintDo, setAutoPrintDo, qzErrorText } from "@/lib/qz";

const MAX_BULK = 50;
const todayJkt = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
const BULK_KINDS = [
  { k: "so", label: "SO", hint: "Sales Order" },
  { k: "do", label: "DO", hint: "Semua Delivery Order dari order terpilih" },
  { k: "invoice", label: "Invoice", hint: "Invoice resmi; Faktur bila order belum ditagih" },
];

function computeTotals(lines, rateBps, mode) {
  const sum = lines.reduce((a, l) => a + Number(l.unit_price || 0) * Number(l.quantity || 0), 0);
  if (mode === "include") {
    const untaxed = rateBps ? Math.round((sum * 10000) / (10000 + rateBps)) : sum;
    return { untaxed, tax: sum - untaxed, total: sum };
  }
  const tax = Math.round((sum * rateBps) / 10000);
  return { untaxed: sum, tax, total: sum + tax };
}

const STATUSES = ["Menunggu", "Diproses", "Siap dikirim", "Pengiriman sebagian", "Delivered"];
const NO_VOID = ["Void", "Dibayar Sebagian", "Lunas", "Pengiriman sebagian"];
const DELIVERED = ["Delivered", "Ditagihkan"];

function odooStateLabel(o) {
  if (!o.odoo_synced) return null;
  if (o.odoo_state === "cancel" || o.status === "Void") return { t: "Odoo: Dibatalkan", c: "cancel" };
  if (o.odoo_delivery_status === "full" || o.odoo_state === "done") return { t: "Odoo: Terkirim", c: "done" };
  if (o.odoo_delivery_status === "partial") return { t: "Odoo: Terkirim Sebagian", c: "sale" };
  if (o.odoo_state === "sale") return { t: "Odoo: Sales Order", c: "sale" };
  if (o.odoo_state === "draft" || o.odoo_state === "sent") return { t: "Odoo: Quotation", c: "draft" };
  return null;
}


function ProductTypeahead({ products, line, onSelect, testId }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const selected = !!line.product_odoo_id;

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const term = q.trim().toLowerCase();
  const matches = (term
    ? products.filter((p) => p.name.toLowerCase().includes(term) || (p.default_code || "").toLowerCase().includes(term))
    : products
  ).slice(0, 40);

  return (
    <div className="product-typeahead" ref={boxRef}>
      <input
        value={open ? q : (selected ? line.product_name : q)}
        onFocus={() => { setOpen(true); setQ(""); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        placeholder={selected ? line.product_name : "Ketik nama / SKU produk…"}
        data-testid={testId} autoComplete="off" />
      {open && (
        <div className="product-suggest" data-testid={`${testId}-suggest`}>
          {matches.length === 0 ? (
            <div className="product-suggest-empty">Produk tidak ditemukan</div>
          ) : matches.map((p) => (
            <button type="button" key={p.odoo_id} className={p.odoo_id === line.product_odoo_id ? "active" : ""}
              onClick={() => { onSelect(p); setOpen(false); setQ(""); }} data-testid={`${testId}-opt-${p.odoo_id}`}>
              <strong>{p.name}</strong>
              <small>{p.default_code || "—"} · {p.uom}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SalesOrders({ newOrderSignal }) {
  const { hasPerm } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [exporting, setExporting] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [activityFor, setActivityFor] = useState(null);
  const [shipmentFor, setShipmentFor] = useState(null);
  const [voidFor, setVoidFor] = useState(null);
  const [closeFor, setCloseFor] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [kinds, setKinds] = useState(new Set(["do"]));
  const [picking, setPicking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/sales", { params: { status: statusFilter, q: q || undefined, start: start || undefined, end: end || undefined } });
      setOrders(r.data.data);
      setSel(new Set());
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [statusFilter, q, start, end]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { if (newOrderSignal) setOrderOpen(true); }, [newOrderSignal]);

  const counts = STATUSES.map((s) => orders.filter((o) => o.status === s).length);
  const pipeline = [
    { label: "Menunggu", value: counts[0], icon: Clock, cls: "amber" },
    { label: "Diproses", value: counts[1], icon: ClipboardList, cls: "red" },
    { label: "Siap dikirim", value: counts[2], icon: Truck, cls: "green" },
    { label: "Sebagian", value: counts[3], icon: Split, cls: "blue" },
    { label: "Delivered", value: counts[4], icon: PackageCheck, cls: "dark" },
  ];

  const doProcess = async (o) => {
    try { const r = await api.post(`/sales/${o.id}/process`); toast.success(`${o.so_number} diproses`); if (r.data?.meta?.odoo_warning) toast.warning(r.data.meta.odoo_warning); load(); }
    catch (e) { toast.error(apiError(e), { duration: 12000, closeButton: true }); }
  };
  const doDelete = async (o) => {
    if (!window.confirm(`Hapus order ${o.so_number}?`)) return;
    try { await api.delete(`/sales/${o.id}`); toast.success("Order dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doVoid = (o) => setVoidFor(o);
  const allOn = sel.size > 0 && (sel.size >= MAX_BULK || orders.filter((o) => o.status !== "Void").every((o) => sel.has(o.id)));
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => {
    if (allOn) { setSel(new Set()); return; }
    const pick = orders.filter((o) => o.status !== "Void").slice(0, MAX_BULK);
    if (pick.length < orders.filter((o) => o.status !== "Void").length) toast.info(`Dipilih ${MAX_BULK} order teratas (maksimal per cetak massal). Persempit filter untuk sisanya.`);
    setSel(new Set(pick.map((o) => o.id)));
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const r = await api.get("/sales/export.xlsx", { params: { status: statusFilter, q: q || undefined, start: start || undefined, end: end || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = `sales-orders-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Excel diunduh");
    } catch (e) { toast.error("Gagal ekspor Excel"); } finally { setExporting(false); }
  };

  return (
    <div data-testid="sales-page">
      <div className="section-head compact">
        <div>
          <p className="eyebrow">PENJUALAN</p>
          <h2>Sales Order</h2>
        </div>
        {hasPerm("orders:create") && (
          <button className="rj-btn rj-btn-primary" onClick={() => setOrderOpen(true)} data-testid="open-order-dialog-btn">
            <Plus size={18} /> Input penjualan
          </button>
        )}
      </div>

      <div className="order-pipeline">
        {pipeline.map((p) => {
          const Icon = p.icon;
          return (
            <div className={`pipeline-card ${p.cls}`} key={p.label} data-testid={`pipeline-${slug(p.label)}`}>
              <div><Icon /></div>
              <div><span>{p.label}</span><strong>{p.value}</strong></div>
            </div>
          );
        })}
      </div>

      <div className="panel table-panel order-table">
        <div className="table-tools">
          <div className="search-box">
            <Search />
            <input data-testid="sales-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari SO / outlet…" />
          </div>
          <div className="sales-date-filter">
            <label>Dari<DateField value={start} onChange={setStart} max={end || undefined} testId="sales-start-date" /></label>
            <label>Sampai<DateField value={end} onChange={setEnd} min={start || undefined} testId="sales-end-date" /></label>
            {(start || end) && <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => { setStart(""); setEnd(""); }} data-testid="sales-date-clear">Reset</button>}
          </div>
          <select className="rj-select" style={{ width: 170 }} value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} data-testid="sales-status-filter">
            <option value="all">Semua Status</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            <option value="Void">Void (Dibatalkan)</option>
            <option value="billed">Sudah ditagih (Finance)</option>
          </select>
          <button className="rj-btn rj-btn-green rj-btn-sm" onClick={exportExcel} disabled={exporting} data-testid="sales-export-btn">
            {exporting ? <Loader2 size={14} className="spin" /> : <FileDown size={14} />} Ekspor Excel
          </button>
          <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => setPicking({ today: true })} data-testid="picking-today-btn"
            title="Barang untuk DO yang diproses hari ini / tanggal kirim hari ini">
            <ClipboardList size={14} /> Picking List Hari Ini
          </button>
        </div>

        <BulkPrintBar count={sel.size} testId="order-bulk-bar" onClear={() => setSel(new Set())}
          summary={<><b data-testid="order-bulk-count">{sel.size}</b> order dipilih · cetak:</>}
          body={() => ({ orders: orders.filter((o) => sel.has(o.id)).map((o) => o.id), order_kinds: BULK_KINDS.map((k) => k.k).filter((k) => kinds.has(k)) })}>
          {BULK_KINDS.map((k) => (
            <label key={k.k} className="autoprint-toggle" title={k.hint} data-testid={`order-bulk-kind-${k.k}`}>
              <input type="checkbox" checked={kinds.has(k.k)} data-testid={`order-bulk-kind-${k.k}-toggle`}
                onChange={() => setKinds((s) => { const n = new Set(s); n.has(k.k) ? n.delete(k.k) : n.add(k.k); return n; })} />
              {k.label}
            </label>
          ))}
          <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => setPicking({ orders: orders.filter((o) => sel.has(o.id)).map((o) => o.id) })}
            data-testid="order-bulk-picking-btn" title="Picking list gudang dari order terpilih"><ClipboardList size={14} /> Picking list</button>
        </BulkPrintBar>
        {picking && <PickingListDialog body={picking} onClose={() => setPicking(null)} />}
        {loading ? (
          <div className="rj-loading"><Loader2 className="spin" /> Memuat order…</div>
        ) : orders.length === 0 ? (
          <div className="rj-empty"><ClipboardList /><p>Belum ada order. Klik "Input penjualan".</p></div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr style={{ background: "#faf8f7", fontSize: 11, color: "#817974", textAlign: "left" }}>
                  <th className="chk-col" style={{ padding: "12px 0 12px 12px" }}><input type="checkbox" checked={allOn} aria-label="Centang semua order"
                    onChange={selectAll} data-testid="orders-select-all" /></th>
                  <th style={{ padding: 12 }}>SO / Outlet</th>
                  <th style={{ padding: 12 }}>Tipe</th>
                  <th style={{ padding: 12 }}>Item</th>
                  <th style={{ padding: 12 }}>Total</th>
                  <th style={{ padding: 12 }}>Status</th>
                  <th style={{ padding: 12 }}>Tanggal</th>
                  <th style={{ padding: 12, textAlign: "right" }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className={sel.has(o.id) ? "row-picked" : ""} style={{ borderTop: "1px solid #f0ecea" }} data-testid={`order-row-${o.so_number}`}>
                    <td className="chk-col" style={{ padding: "12px 0 12px 12px" }}><input type="checkbox" checked={sel.has(o.id)} aria-label={`Pilih ${o.so_number}`}
                      onChange={() => toggle(o.id)} data-testid={`order-check-${o.so_number}`} /></td>
                    <td style={{ padding: 12 }}>
                      <strong style={{ display: "block", fontSize: 13 }}>{o.outlet}</strong>
                      <span className="odoo-ref">{o.so_number}</span>
                      <span className={`rj-odoo-badge ${o.odoo_synced ? "ok" : "pending"}`} data-testid={`odoo-status-${o.so_number}`}>
                        {o.odoo_synced ? <><CheckCircle2 size={11} /> Terkirim ke Odoo</> : "Belum di Odoo"}
                      </span>
                      {(() => { const s = odooStateLabel(o); return s ? <span className={`rj-odoo-state ${s.c}`} data-testid={`odoo-state-${o.so_number}`}>{s.t}</span> : null; })()}
                    </td>
                    <td style={{ padding: 12 }}>
                      <span className={`customer-type ${slug(o.customer_type)}`}>{o.customer_type}</span>
                    </td>
                    <td style={{ padding: 12, fontSize: 13 }}>{o.items}</td>
                    <td style={{ padding: 12, fontWeight: 800, fontSize: 13 }}>{rupiah(o.amount)}</td>
                    <td style={{ padding: 12 }}>
                      <span className={`order-status ${slug(o.status)}`}>{o.status}</span>
                      {o.status === "Void" && o.void_reason && (
                        <small className="void-reason-inline" title={o.void_reason} data-testid={`void-reason-${o.so_number}`}>Alasan: {o.void_reason}</small>
                      )}
                      {o.billing_status === "partial" && <span className="bill-chip partial" data-testid={`billing-chip-${o.so_number}`}>Ditagih sebagian</span>}
                      {o.closed_at && <span className="bill-chip closed" title={o.close_reason} data-testid={`closed-chip-${o.so_number}`}>Ditutup · sisa batal</span>}
                      {o.void_request?.status === "pending" && <span className="bill-chip void-pending" data-testid={`void-pending-${o.so_number}`}>Menunggu approval void</span>}
                      {o.credit_hold?.status === "pending" && <span className="bill-chip credit-hold" title={`Lebih ${rupiah(o.credit_hold.over_by)} dari batas kredit`} data-testid={`credit-hold-chip-${o.so_number}`}>Menunggu approval kredit</span>}
                      {o.void_request?.status === "rejected" && o.status !== "Void" && <span className="bill-chip void-rejected">Void ditolak</span>}
                    </td>
                    <td style={{ padding: 12, fontSize: 12, color: "#7b7470" }}>{fmtDate(o.created_at)}
                      {o.delivery_date && <small className="ship-date-chip" data-testid={`delivery-date-${o.so_number}`}>Kirim {fmtDate(o.delivery_date)}</small>}
                    </td>
                    <td style={{ padding: 12 }}>
                      <div className="row-actions">
                        <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => setActivityFor(o)}
                          data-testid={`order-activity-${o.so_number}`} title="Aktivitas"><FileClock size={14} /></button>
                        {hasPerm("orders:process") && o.status === "Menunggu" && o.credit_hold?.status !== "pending" && (
                          <button className="rj-btn rj-btn-green rj-btn-sm" onClick={() => doProcess(o)}
                            data-testid={`order-process-${o.so_number}`}>Proses</button>
                        )}
                        {hasPerm("delivery:manage") && ["Siap dikirim", "Pengiriman sebagian"].includes(o.status) && (
                          <button className="rj-btn rj-btn-primary rj-btn-sm" onClick={() => setShipmentFor(o)}
                            data-testid={`order-deliver-${o.so_number}`}><Truck size={14} /> Kirim</button>
                        )}
                        <DocMenu title={`Dokumen ${o.so_number}`} load={() => api.get(`/documents/order/${o.id}`).then((r) => r.data.data)}
                          testId={`order-docs-${o.so_number}`} />
                        {hasPerm("orders:close") && o.status === "Pengiriman sebagian" && (
                          <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => setCloseFor(o)}
                            data-testid={`order-close-${o.so_number}`} title="Tutup order — sisa tidak dikirim, langsung ditagih"><PackageCheck size={14} /> Tutup</button>
                        )}
                        {hasPerm("orders:void") && !NO_VOID.includes(o.status) && !["partial", "paid"].includes(o.payment_status) && o.void_request?.status !== "pending" && (
                          <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => doVoid(o)}
                            data-testid={`order-void-${o.so_number}`} title="Void / Batalkan transaksi"><Ban size={14} /></button>
                        )}
                        {hasPerm("orders:delete") && ["Menunggu"].includes(o.status) && (
                          <button className="rj-btn rj-btn-danger rj-btn-sm" onClick={() => doDelete(o)}
                            data-testid={`order-delete-${o.so_number}`}><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {orderOpen && <OrderDialog onClose={() => setOrderOpen(false)} onSaved={() => { setOrderOpen(false); load(); }} />}
      {activityFor && <ActivityDialog order={activityFor} onClose={() => setActivityFor(null)} onChanged={load} />}
      {shipmentFor && <ShipmentDialog order={shipmentFor} onClose={() => setShipmentFor(null)} onSaved={() => { setShipmentFor(null); load(); }} />}
      {voidFor && <VoidDialog order={voidFor} onClose={() => setVoidFor(null)} onSaved={() => { setVoidFor(null); load(); }} />}
      {closeFor && <CloseOrderDialog order={closeFor} onClose={() => setCloseFor(null)} onSaved={() => { setCloseFor(null); load(); }} />}
    </div>
  );
}

/* ---------------- Void dialog ---------------- */
function VoidDialog({ order, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const valid = reason.trim().length >= 3;
  const needsApproval = DELIVERED.includes(order.status) || !!order.billing_status;

  const submit = async () => {
    if (!valid) { toast.error("Alasan void wajib diisi (minimal 3 karakter)"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/sales/${order.id}/void`, { reason: reason.trim() });
      if (r.data?.meta?.pending_approval) toast.success(`Permintaan void ${order.so_number} dikirim ke menu Approval`);
      else toast.success(`${order.so_number} dibatalkan`);
      if (r.data?.meta?.odoo_warning) toast.warning(r.data.meta.odoo_warning);
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Void Transaksi ${order.so_number}`} subtitle={order.outlet} testId="void-dialog"
      footer={<>
        <button className="rj-btn rj-btn-ghost" onClick={onClose} data-testid="void-cancel-btn">Batal</button>
        <button className="rj-btn rj-btn-primary" onClick={submit} disabled={saving || !valid} data-testid="void-submit-btn">
          {saving ? <Loader2 size={16} className="spin" /> : <><Ban size={16} /> {needsApproval ? "Ajukan Void ke Approval" : "Void Transaksi"}</>}
        </button>
      </>}>
      {needsApproval && (
        <div className="fin-info" data-testid="void-needs-approval"><AlertTriangle size={15} />
          <span>Barang order ini sudah terkirim. Void membutuhkan persetujuan di menu Approval. Setelah disetujui: barang otomatis diretur ke stok di Odoo, invoice (jika ada) dibatalkan dengan nota kredit, lalu SO dibatalkan.</span></div>
      )}
      <div className="void-warning"><AlertTriangle size={16} /> Transaksi tetap tersimpan sebagai dibatalkan, tidak dihitung di Dashboard, dan tidak bisa diproses lagi. Sale order di Odoo juga dibatalkan.</div>
      <label className="rj-field"><span>Alasan Void <span className="void-required">*wajib</span></span>
        <textarea className="rj-input void-reason-input" rows={4} value={reason} autoFocus
          onChange={(e) => setReason(e.target.value)} placeholder="cth: Customer membatalkan pesanan karena stok toko masih penuh"
          data-testid="void-reason-input" />
      </label>
    </Modal>
  );
}

// Silent dot-matrix print via QZ Tray; falls back to the PDF when no printer / QZ Tray is unreachable.
async function autoPrintDo(doc, win) {
  try {
    if (await printRaw(doc)) { win?.close(); return; }
    toast.info("Printer dot-matrix belum diatur — PDF DO dibuka untuk dicetak manual");
  } catch (e) {
    toast.warning(`Print otomatis gagal (${e?.response ? apiError(e) : qzErrorText(e)}) — PDF DO dibuka untuk dicetak manual`);
  }
  try { await openPdf(doc, win); } catch (e) { win?.close(); toast.error(`Gagal membuka PDF DO: ${apiError(e)}`); }
}

/* ---------------- Order entry dialog ---------------- */
function OrderDialog({ onClose, onSaved, editOrder }) {
  const { hasPerm } = useAuth();
  const editing = !!editOrder;
  const [master, setMaster] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addressId, setAddressId] = useState("");
  const [warehouseId, setWarehouseId] = useState(1);
  const [taxId, setTaxId] = useState(1);
  const [taxMode, setTaxMode] = useState("exclude");
  const [note, setNote] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [lines, setLines] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/odoo/master").then(async (r) => {
      const m = r.data.data;
      if (!editing) {
        if (m.warehouses[0]) setWarehouseId(m.warehouses[0].odoo_id);
        if (m.taxes[0]) setTaxId(m.taxes[0].odoo_id);
        setMaster(m);
        return;
      }
      try {
        const [c, d] = await Promise.all([api.get(`/customers/${editOrder.customer_odoo_id}`), api.get(`/sales/${editOrder.id}`)]);
        const cust = c.data.data;
        const addrs = cust.delivery_addresses || [];
        const addr = addrs.find((a) => a.odoo_id && a.odoo_id === editOrder.shipping_partner_odoo_id) || addrs.find((a) => a.street === editOrder.shipping_address) || addrs[0];
        setCustomer(cust);
        setAddressId(addr?.id || "");
        setWarehouseId(editOrder.warehouse_odoo_id || m.warehouses[0]?.odoo_id);
        setTaxId(editOrder.tax_odoo_id || m.taxes[0]?.odoo_id);
        setNote(editOrder.internal_note || "");
        setDeliveryDate(editOrder.delivery_date || "");
        setLines((d.data.data.lines || []).map((l) => ({ product_odoo_id: l.product_odoo_id, product_name: l.product_name, uom: l.uom,
          quantity: l.quantity, unit_price: l.unit_price, price_overridden: !!l.price_overridden })));
        setMaster(m);
      } catch (e) { toast.error(apiError(e)); onClose(); }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enabledProducts = (master?.products || []).filter((p) => p.active && p.sales_hub_enabled);
  const tax = (master?.taxes || []).find((t) => t.odoo_id === Number(taxId));
  const rateBps = tax?.rate_bps || 0;
  useEffect(() => { if (tax) setTaxMode(tax.price_included ? "include" : "exclude"); }, [taxId]); // eslint-disable-line

  const priceFor = async (pricelistId, productId, qty) => {
    try {
      const r = await api.get("/odoo/prices", { params: { pricelist_odoo_id: pricelistId, product_odoo_id: productId, quantity: qty } });
      return r.data.data.price;
    } catch { return 0; }
  };
  // Editing the same customer keeps the order's own pricelist; a new customer uses theirs.
  const sameCustomer = editing && customer?.odoo_customer_id === editOrder.customer_odoo_id;
  const pricelistId = sameCustomer ? editOrder.pricelist_odoo_id || customer?.pricelist_odoo_id : customer?.pricelist_odoo_id;
  const resolvePrice = useCallback(async (productId, qty) => (customer ? priceFor(pricelistId, productId, qty) : 0), [customer, pricelistId]);

  // Changing customer re-prices non-manual lines with the new customer's pricelist.
  const pickCustomer = async (c) => {
    setCustomer(c);
    setAddressId((c.delivery_addresses || [])[0]?.id || "");
    const repriced = await Promise.all(lines.map(async (l) => (l.product_odoo_id && !l.price_overridden
      ? { ...l, unit_price: await priceFor(c.pricelist_odoo_id, l.product_odoo_id, Number(l.quantity) || 1) } : l)));
    setLines(repriced);
  };

  const addEmptyLine = () => setLines((ls) => [...ls, { product_odoo_id: null, product_name: "", uom: "", quantity: 1, unit_price: 0, price_overridden: false }]);

  const selectLineProduct = async (i, p) => {
    const qty = Number(lines[i]?.quantity) || 1;
    updateLine(i, { product_odoo_id: p.odoo_id, product_name: p.name, uom: p.uom, price_overridden: false });
    const price = await resolvePrice(p.odoo_id, qty);
    updateLine(i, { unit_price: price });
  };

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const ok = [];
    for (const f of picked) {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) { toast.error(`${f.name}: tipe tidak didukung`); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: melebihi 10MB`); continue; }
      ok.push(f);
    }
    if (ok.length) setFiles((fs) => [...fs, ...ok]);
    e.target.value = "";
  };

  const updateLine = (i, patch) => setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const onQtyChange = async (i, qty) => {
    updateLine(i, { quantity: qty });
    const l = lines[i];
    if (l && !l.price_overridden) {
      const price = await resolvePrice(l.product_odoo_id, qty || 1);
      updateLine(i, { quantity: qty, unit_price: price });
    }
  };

  const totals = computeTotals(lines, rateBps, taxMode);

  const submit = async () => {
    if (!customer) { toast.error("Pilih customer terlebih dahulu"); return; }
    const validLines = lines.filter((l) => l.product_odoo_id);
    if (validLines.length === 0) { toast.error("Tambahkan minimal satu produk"); return; }
    if (validLines.some((l) => !l.quantity || l.quantity <= 0)) { toast.error("Quantity harus lebih dari 0"); return; }
    const addr = (customer.delivery_addresses || []).find((a) => a.id === addressId) || (customer.delivery_addresses || [])[0];
    setSaving(true);
    try {
      const payload = {
        customer_odoo_id: customer.odoo_customer_id,
        shipping_partner_odoo_id: addr?.odoo_id,
        shipping_address_name: addr?.name,
        shipping_address: addr?.street,
        pricelist_odoo_id: pricelistId,
        distributor_name: sameCustomer ? editOrder.distributor_name : undefined,
        warehouse_odoo_id: Number(warehouseId),
        tax_odoo_id: Number(taxId),
        tax_mode: taxMode,
        fulfillment_mode: customer.fulfillment_mode === "direct" ? "Direct" : "Distributor",
        internal_note: note,
        delivery_date: deliveryDate || null,
        lines: validLines.map((l) => ({ product_odoo_id: l.product_odoo_id, quantity: Number(l.quantity), unit_price: l.unit_price, price_overridden: l.price_overridden })),
      };
      if (editing) {
        const r = await api.patch(`/sales/${editOrder.id}`, payload);
        const n = r.data?.meta?.changes || 0;
        if (!n) toast.info("Tidak ada perubahan pada SO");
        else if (r.data?.meta?.credit_hold?.status === "pending") toast.warning(`SO diperbarui (${n} perubahan) — melewati batas kredit, menunggu persetujuan Finance`);
        else toast.success(`${editOrder.so_number} diperbarui (${n} perubahan)`);
        onSaved();
        return;
      }
      const res = await api.post("/sales", payload);
      const newId = res.data?.data?.id;
      if (newId && files.length && hasPerm("orders:attachments")) {
        toast.message(`Mengunggah ${files.length} lampiran…`);
        for (const f of files) {
          try {
            const fd = new FormData();
            fd.append("file", f);
            await api.post(`/sales/${newId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          } catch (err) { toast.error(`Gagal unggah ${f.name}`); }
        }
      }
      if (res.data?.meta?.credit_hold) toast.warning("Order melewati batas kredit customer — menunggu persetujuan Finance di menu Approval");
      else toast.success("Sales Order berhasil dibuat");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} className="order-entry-dialog" title={editing ? `Edit ${editOrder.so_number}` : "Input Penjualan"}
      subtitle={editing ? "Perubahan dicatat di Riwayat & ikut diperbarui di Odoo" : "Buat Sales Order baru (nomor SO mengikuti Odoo)"} testId={editing ? "order-edit-dialog" : "order-dialog"}
      footer={
        <>
          <button className="rj-btn rj-btn-ghost" onClick={onClose}>Batal</button>
          <button className="rj-btn rj-btn-primary" onClick={submit} disabled={saving} data-testid="submit-order-btn">
            {saving ? <Loader2 size={16} className="spin" /> : (editing ? "Simpan Perubahan" : "Simpan & Buat SO")}
          </button>
        </>
      }>
      {!master ? <div className="rj-loading"><Loader2 className="spin" /> Memuat master data…</div> : (
        <div className="order-builder">
          <div className="order-section customer-section">
            <div className="order-section-title">
              <div><b>1</b><div><strong>Customer & Pengiriman</strong><small>Pilih customer restricted POS</small></div></div>
            </div>
            <div className="customer-picker-field">
              <button type="button" className={`customer-picker-trigger ${customer ? "selected" : ""}`}
                onClick={() => setPickerOpen(true)} data-testid="pick-customer-btn">
                <span className="customer-picker-icon"><Store size={18} /></span>
                {customer ? (
                  <span><strong>{customer.name}</strong><small>{customer.customer_type} · {customer.area}</small></span>
                ) : (
                  <span><strong>Pilih customer</strong><small>Klik untuk mencari</small></span>
                )}
                <b>{customer ? "Ganti" : "Cari"}</b>
              </button>
            </div>
            {customer && (
              <>
                <div className="customer-auto-card">
                  <div><span>Tipe</span><strong>{customer.customer_type}</strong></div>
                  <div><span>Pricelist</span><strong>{customer.price_list}</strong></div>
                  <div><span>Credit Limit</span><strong>{rupiahShort(customer.credit_limit)}</strong></div>
                  <small><Info size={13} /> Pemenuhan: {customer.fulfillment_mode === "direct" ? "Direct Rasa Juara" : "Melalui Distributor"}</small>
                </div>
                <CustomerArWarning customerId={customer.odoo_customer_id} orderAmount={totals.total} />
                <label>Alamat Kirim
                  <select className="rj-select" value={addressId} onChange={(e) => setAddressId(e.target.value)} data-testid="order-address-select">
                    {(customer.delivery_addresses || []).map((a) => (
                      <option key={a.id} value={a.id}>{a.name} — {a.street}</option>
                    ))}
                  </select>
                </label>
                <label>Tanggal Kirim <small className="fin-muted">(opsional)</small>
                  <DateField value={deliveryDate} onChange={setDeliveryDate} min={todayJkt()} testId="order-delivery-date" />
                </label>
              </>
            )}
          </div>

          <div className="order-section">
            <div className="order-section-title">
              <div><b>2</b><div><strong>Warehouse & Pajak</strong><small>Data dari Odoo</small></div></div>
            </div>
            <div className="order-fields-grid">
              <label>Warehouse Asal
                <select className="rj-select" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} data-testid="order-warehouse-select">
                  {master.warehouses.map((w) => <option key={w.odoo_id} value={w.odoo_id}>{w.name}</option>)}
                </select>
              </label>
              <label>Pajak (Tax)
                <select className="rj-select" value={taxId} onChange={(e) => setTaxId(e.target.value)} data-testid="order-tax-select">
                  {master.taxes.map((t) => <option key={t.odoo_id} value={t.odoo_id}>{t.name} ({(t.rate_bps / 100)}%)</option>)}
                </select>
              </label>
              <label>Mode PPN
                <select className="rj-select" value={taxMode} disabled title="Otomatis mengikuti sifat pajak Odoo (Incl/Excl)" data-testid="order-taxmode-select">
                  <option value="exclude">Exclude (ditambah di atas)</option>
                  <option value="include">Include (sudah termasuk)</option>
                </select>
              </label>
            </div>
          </div>

          <div className="order-section">
            <div className="order-section-title">
              <div><b>3</b><div><strong>Detail Produk</strong><small>Ketik untuk cari, pilih dari saran</small></div></div>
              {lines.length > 0 && (
                <button type="button" className="rj-btn rj-btn-green rj-btn-sm" onClick={addEmptyLine} data-testid="order-add-line-btn">
                  <Plus size={15} /> Tambah baris
                </button>
              )}
            </div>
            {lines.length === 0 ? (
              <button type="button" className="order-empty order-empty-add" onClick={addEmptyLine} data-testid="order-empty-add-btn">
                <Plus />
                <div><strong>Tambah produk pertama</strong><span>Klik lalu ketik nama / SKU produk.</span></div>
              </button>
            ) : (
              <>
                <div className="product-lines">
                  {lines.map((l, i) => (
                    <div className="product-line" key={i} data-testid={`order-line-${i}`}>
                      <span className="line-number">{i + 1}</span>
                      <label>Produk
                        <ProductTypeahead products={enabledProducts} line={l}
                          onSelect={(p) => selectLineProduct(i, p)} testId={`order-line-product-${i}`} />
                      </label>
                      <label>Qty<input type="number" min="1" value={l.quantity}
                        onChange={(e) => updateLine(i, { quantity: e.target.value })}
                        onBlur={(e) => onQtyChange(i, Number(e.target.value))} data-testid={`order-line-qty-${i}`} /></label>
                      <label>Harga Satuan
                        <input type="number" value={l.unit_price}
                          onChange={(e) => updateLine(i, { unit_price: Number(e.target.value), price_overridden: true })}
                          disabled={!hasPerm("orders:update") && !hasPerm("orders:create")} data-testid={`order-line-price-${i}`} />
                        {l.price_overridden && <small style={{ color: "#b80000" }}>harga manual</small>}
                      </label>
                      <div className="line-subtotal">
                        <span>Subtotal</span>
                        <strong>{rupiah(Number(l.unit_price) * Number(l.quantity || 0))}</strong>
                      </div>
                      <button onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} title="Hapus" data-testid={`order-line-remove-${i}`}><Trash2 /></button>
                    </div>
                  ))}
                </div>
                <button type="button" className="add-line-btn" onClick={addEmptyLine} data-testid="order-add-line-btn-2">
                  <Plus size={16} /> Tambah baris produk
                </button>
              </>
            )}
          </div>

          {hasPerm("orders:attachments") && !editing && (
            <div className="order-section">
              <div className="order-section-title">
                <div><b>4</b><div><strong>Lampiran PO</strong><small>PDF/JPG/PNG · dikompres otomatis · maks 10MB</small></div></div>
              </div>
              <label className="order-attachment-box" data-testid="order-attachment-label">
                <Upload /> <span>Pilih file PO (bisa lebih dari satu)</span>
                <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={onPickFiles} data-testid="order-attachment-input" />
              </label>
              {files.length > 0 && (
                <div className="order-attachment-chips">
                  {files.map((f, i) => (
                    <div className="attachment-chip" key={i} data-testid={`order-attachment-chip-${i}`}>
                      <Paperclip size={13} />
                      <span className="chip-name">{f.name}</span>
                      <small>{(f.size / 1024).toFixed(0)} KB</small>
                      <button type="button" onClick={() => setFiles((fs) => fs.filter((_, idx) => idx !== i))} aria-label="Hapus"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="order-finish">
            <label>Catatan Internal
              <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan untuk tim…" data-testid="order-note-input" />
            </label>
            <div className="order-totals" data-testid="order-totals">
              <div><span>Subtotal (DPP)</span><span>{rupiah(totals.untaxed)}</span></div>
              <div><span>PPN {(rateBps / 100)}% ({taxMode})</span><span>{rupiah(totals.tax)}</span></div>
              <div className="grand-total"><span>Total Order</span><strong>{rupiah(totals.total)}</strong></div>
            </div>
          </div>
        </div>
      )}
      <CustomerPickerDialog open={pickerOpen} onClose={() => setPickerOpen(false)}
        onSelect={pickCustomer} />
    </Modal>
  );
}

/* ---------------- Activity dialog ---------------- */
function ActivityDialog({ order: initialOrder, onClose, onChanged }) {
  const { hasPerm } = useAuth();
  const [order, setOrder] = useState(initialOrder);
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState(null);
  const [lines, setLines] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [act, det, shp] = await Promise.all([
      api.get(`/sales/${order.id}/activity`),
      api.get(`/sales/${order.id}`),
      api.get(`/sales/${order.id}/shipments`),
    ]);
    setData(act.data.data);
    setOrder((o) => ({ ...o, ...det.data.data }));
    setLines(det.data.data.lines || []);
    setShipments(shp.data.data || []);
  }, [order.id]);
  useEffect(() => { load(); }, [load]);
  const canEdit = (hasPerm("orders:update") || hasPerm("orders:create")) && ["Menunggu", "Perlu review"].includes(order.status)
    && order.void_request?.status !== "pending" && [null, undefined, "draft", "sent"].includes(order.odoo_state);

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await api.post(`/sales/${order.id}/activity`, { body: note }); setNote(""); load(); toast.success("Catatan ditambahkan"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { await api.post(`/sales/${order.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Lampiran diunggah"); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };

  const download = async (a) => {
    try {
      const r = await api.get(`/sales/attachments/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Modal open onClose={onClose} className="activity-dialog" title={`Aktivitas ${order.so_number}`}
      subtitle={order.outlet} testId="activity-dialog">
      {!data ? <div className="activity-loading"><Loader2 className="spin" /> Memuat…</div> : (
        <>
        {order.status === "Void" && (
          <div className="void-banner" data-testid="activity-void-banner">
            <Ban size={18} />
            <div>
              <strong>Transaksi dibatalkan (Void)</strong>
              <p data-testid="activity-void-reason">Alasan: {order.void_reason || "—"}</p>
              <small>{order.voided_by_name || order.voided_by_email || "—"}{order.voided_at ? ` · ${fmtDateTime(order.voided_at)}` : ""}</small>
            </div>
          </div>
        )}
        <section className="activity-lines">
          <h4><Package size={16} /> Item Produk ({lines.length})</h4>
          {lines.length === 0 ? (
            <p style={{ fontSize: 12, color: "#8b837f" }}>Tidak ada rincian produk.</p>
          ) : (
            <div className="mini-line-table">
              <div className="mini-line-row head"><span>Produk</span><span>Qty</span><span>Harga</span><span>Subtotal</span></div>
              {lines.map((l) => (
                <div className="mini-line-row" key={l.id} data-testid={`activity-line-${l.product_odoo_id}`}>
                  <strong>{l.product_name}</strong>
                  <span>{l.quantity} {l.uom || ""}</span>
                  <span>{rupiah(l.unit_price)}</span>
                  <span>{rupiah(l.subtotal ?? l.unit_price * l.quantity)}</span>
                </div>
              ))}
            </div>
          )}
        </section>
        <div className="activity-totals" data-testid="activity-totals">
          <div><span>Subtotal (DPP)</span><span>{rupiah(order.untaxed_amount ?? 0)}</span></div>
          <div><span>PPN {((order.tax_rate_bps || 0) / 100)}% ({order.tax_mode || "-"})</span><span>{rupiah(order.tax_amount ?? 0)}</span></div>
          <div className="grand"><span>Total</span><strong>{rupiah(order.amount ?? 0)}</strong></div>
        </div>
        {shipments.length > 0 && (
          <section className="shipment-history" data-testid="shipment-history">
            <h4><Truck size={16} /> Riwayat Pengiriman ({shipments.length})</h4>
            <div className="ship-hist-list">
              {shipments.map((sh) => (
                <div className="ship-hist-row" key={sh.id} data-testid={`shipment-${sh.sequence}`}>
                  <div>
                    <strong>Pengiriman #{sh.sequence}{sh.partial ? " · Sebagian" : ""}</strong>
                    <span>{sh.odoo_picking_name} · {fmtDateTime(sh.delivered_at || sh.created_at)}</span>
                    <small>{(sh.lines || []).length} produk · {(sh.lines || []).reduce((a, l) => a + (l.shipped_qty || 0), 0)} unit{sh.partial ? " · backorder aktif" : ""}</small>
                  </div>
                  <DocActions doc={{ kind: "do", id: sh.id, label: "Delivery Order", number: sh.odoo_picking_name }} testId={`shipment-do-${sh.sequence}`} />
                </div>
              ))}
            </div>
          </section>
        )}
        <div className="activity-layout">
          <section>
            <div className="riwayat-head">
              <h4><FileClock size={16} /> Riwayat</h4>
              {canEdit && (
                <button className="rj-btn rj-btn-green rj-btn-sm" onClick={() => setEditing(true)} data-testid="activity-edit-so-btn"><Pencil size={14} /> Edit SO</button>
              )}
            </div>
            {order.edit_count > 0 && <small className="riwayat-edited" data-testid="activity-edit-count">Diedit {order.edit_count}x · terakhir oleh {order.updated_by_name || order.updated_by_email} · {fmtDateTime(order.updated_at)}</small>}
            <div className="audit-list" data-testid="activity-audit-list">
              <AuditTimeline items={data.audit} />
            </div>
          </section>
          <section>
            <h4><Paperclip size={16} /> Catatan & Lampiran</h4>
            {hasPerm("orders:notes") && (
              <div className="note-compose">
                <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tulis catatan…" data-testid="note-input" />
                <button className="rj-btn rj-btn-green rj-btn-sm" onClick={addNote} disabled={busy} data-testid="add-note-btn">Tambah Catatan</button>
              </div>
            )}
            <div className="notes-list">
              {data.notes.map((n) => (
                <article key={n.id}><p>{n.body}</p><small>{n.actor_email} · {fmtDateTime(n.created_at)}</small></article>
              ))}
            </div>
            {hasPerm("orders:attachments") && (
              <label className="upload-box" data-testid="attachment-upload">
                <Upload /> Unggah PO (pdf, jpg, png, doc, docx · maks 10MB)
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={upload} />
              </label>
            )}
            <div className="attachment-list">
              {data.attachments.map((a) => (
                <div key={a.id}>
                  <a onClick={() => download(a)} style={{ cursor: "pointer" }}><Paperclip /> {a.file_name}<small>{Math.round(a.size_bytes / 1024)} KB</small></a>
                </div>
              ))}
            </div>
          </section>
        </div>
        </>
      )}
      {editing && <OrderDialog editOrder={order} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); onChanged?.(); }} />}
    </Modal>
  );
}

/* ---------------- Shipment / delivery dialog ---------------- */
function ShipmentDialog({ order, onClose, onSaved }) {
  const [avail, setAvail] = useState(null);
  const [toShip, setToShip] = useState({});
  const [action, setAction] = useState("backorder");
  const [saving, setSaving] = useState(false);
  const printer = getPrinter();
  const [autoPrint, setAutoPrint] = useState(getAutoPrintDo());

  useEffect(() => {
    api.get(`/sales/${order.id}/delivery`).then((r) => {
      setAvail(r.data.data);
      const init = {};
      r.data.data.lines.forEach((l) => { init[l.product_odoo_id] = l.remaining_qty; });
      setToShip(init);
    });
  }, [order.id]);

  const isPartial = avail?.lines?.some((l) => (Number(toShip[l.product_odoo_id]) || 0) < l.remaining_qty);

  const submit = async () => {
    setSaving(true);
    // No printer: open a tab now (inside the click) so the DO PDF isn't blocked as a popup.
    const win = autoPrint && !printer ? window.open("", "_blank") : null;
    if (win) win.document.write("<p style='font-family:sans-serif;padding:24px'>Menyiapkan PDF DO…</p>");
    try {
      const r = await api.post(`/sales/${order.id}/delivery`, {
        lines: avail.lines.map((l) => ({ product_odoo_id: l.product_odoo_id, ship_qty: Number(toShip[l.product_odoo_id]) || 0 })),
        remaining_action: isPartial ? action : "backorder",
      });
      toast.success("Pengiriman disimpan");
      if (r.data?.meta?.odoo_warning) toast.warning(r.data.meta.odoo_warning);
      const sid = r.data?.meta?.shipment_id;
      if (autoPrint && sid) autoPrintDo({ kind: "do", id: sid }, win);
      else win?.close();
      onSaved();
    } catch (e) { win?.close(); toast.error(apiError(e), { duration: 12000, closeButton: true }); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} className="shipment-dialog" title={`Kirim ${order.so_number}`}
      subtitle="Cek ketersediaan & kirim (mendukung pengiriman sebagian)" testId="shipment-dialog"
      footer={
        <>
          <label className="autoprint-toggle light" data-testid="ship-autoprint">
            <input type="checkbox" checked={autoPrint} onChange={(e) => { setAutoPrint(e.target.checked); setAutoPrintDo(e.target.checked); }} data-testid="ship-autoprint-toggle" />
            {printer ? `Print DO otomatis ke ${printer}` : "Buka PDF DO otomatis (printer dot-matrix belum diatur)"}
          </label>
          <button className="rj-btn rj-btn-ghost" onClick={onClose}>Batal</button>
          <button className="rj-btn rj-btn-primary" onClick={submit} disabled={saving} data-testid="submit-shipment-btn">
            {saving ? <Loader2 size={16} className="spin" /> : "Validasi Pengiriman"}
          </button>
        </>
      }>
      {!avail ? <div className="shipment-loading"><Loader2 /> Memuat ketersediaan…</div> : (
        <>
          <div className="shipment-lines">
            <div className="shipment-line shipment-line-head"><span>Produk</span><span>Order</span><span>Tersedia</span><span>Kirim</span></div>
            {avail.lines.map((l) => (
              <div className="shipment-line" key={l.product_odoo_id}>
                <span><strong style={{ fontSize: 13 }}>{l.product_name}</strong><small>sisa {l.remaining_qty} {l.uom}</small></span>
                <span>{l.ordered_qty}</span>
                <span>{l.available_qty}</span>
                <input type="number" min="0" max={l.remaining_qty} value={toShip[l.product_odoo_id] ?? 0}
                  onChange={(e) => setToShip((s) => ({ ...s, [l.product_odoo_id]: Math.min(Number(e.target.value), l.remaining_qty) }))}
                  data-testid={`ship-qty-${l.product_odoo_id}`} />
              </div>
            ))}
          </div>
          <div className="shipment-totals">
            <span>Status pengiriman</span>
            <b className={isPartial ? "availability-partial" : "availability-ready"}>{isPartial ? "Sebagian (partial)" : "Penuh"}</b>
          </div>
          {isPartial && (
            <div className="followup-choice" style={{ marginTop: 14 }}>
              <button className={action === "backorder" ? "selected" : ""} onClick={() => setAction("backorder")} data-testid="ship-backorder">
                <Truck /><span><strong>Buat pengiriman lanjutan (backorder)</strong><small>Sisa produk dikirim menyusul</small></span>
              </button>
              <button className={`${action === "cancel" ? "selected danger" : ""}`} onClick={() => setAction("cancel")} data-testid="ship-cancel-remaining">
                <AlertTriangle /><span><strong>Batalkan sisa produk</strong><small>Order dianggap selesai (delivered)</small></span>
              </button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
